/* 
 * PROHIBIDO MODIFICAR SIN AUTORIZACIÓN 
 * Este archivo contiene la gestión de campos personalizados agrupados por carpetas y campos protegidos del sistema.
 * Implementado por: Trae AI
 * Fecha: 05/03/2026
 */
import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  makeStyles,
  Container,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Chip,
  Typography,
  Box
} from "@material-ui/core";
import { DeleteOutline, Edit, Search, FolderOpen, Folder, ExpandMore, ExpandLess } from "@material-ui/icons";
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import api from "../../services/api";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 400,
  },
  chip: {
      marginLeft: theme.spacing(1),
      height: 20,
      fontSize: 10,
  },
  folderHeader: {
      backgroundColor: 'rgba(255,255,255,0.05)',
  },
  folderRow: {
      backgroundColor: 'rgba(255,255,255,0.02)',
  },
  folderIcon: {
      marginRight: theme.spacing(1),
      verticalAlign: 'middle',
      color: theme.palette.primary.main
  }
}));

const SYSTEM_FOLDER = { id: 'system_folder', name: 'Sistema' };
const UNASSIGNED_FOLDER = { id: 'unassigned_folder', name: 'Sin Carpeta' };

const CustomFieldsSettings = () => {
  const classes = useStyles();
  const [fields, setFields] = useState([]);
  const [folders, setFolders] = useState([]);
  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("");
  
  // Accordion State
  const [expandedFolders, setExpandedFolders] = useState({ [SYSTEM_FOLDER.id]: true });

  const toggleFolder = (folderId) => {
      setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  // Field Modal State
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldName, setFieldName] = useState("");
  const [fieldDescription, setFieldDescription] = useState("");
  const [fieldType, setFieldType] = useState("Texto");
  const [fieldFolder, setFieldFolder] = useState("");

  // Folder Modal State
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState("");

  // Confirmation Modal State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // 'field' or 'folder'
  
  // Folder Management
  const [folderManagementOpen, setFolderManagementOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/settings");
      
      let loadedFields = [];
      const registry = data.find(s => s.key === "customFieldRegistry");
      if (registry && registry.value) {
          try {
              loadedFields = JSON.parse(registry.value);
          } catch (e) {
              console.error("Invalid JSON for customFieldRegistry", e);
          }
      }

      const systemFields = [
          { 
              id: 'sys-date', 
              name: 'Fecha', 
              description: 'Campo del sistema que representa la fecha actual con formato Día {dd} de {mes} de {aaaa}', 
              type: 'Fecha', 
              system: true, 
              folderId: SYSTEM_FOLDER.id 
          },
          { 
              id: 'sys-name', 
              name: 'Nombre Cliente', 
              description: 'Nombres Completo', 
              type: 'Texto', 
              system: true, 
              folderId: SYSTEM_FOLDER.id 
          }
      ];

      // Add system fields if not present
      let updated = false;
      systemFields.forEach(sysField => {
          if (!loadedFields.find(f => f.name === sysField.name && f.system)) {
              loadedFields.unshift(sysField);
              updated = true;
          }
      });
      
      setFields(loadedFields);

      // Save if new system fields were added
      if (updated) {
          saveFields(loadedFields, true); // true = silent save
      }

      const foldersReg = data.find(s => s.key === "customFieldFolders");
      if (foldersReg && foldersReg.value) {
          try {
              setFolders(JSON.parse(foldersReg.value));
          } catch (e) {
              console.error("Invalid JSON for customFieldFolders", e);
              setFolders([]);
          }
      }
    } catch (e) {
      toast.error("Error al cargar datos");
    }
  };

  const saveFields = async (newFields, silent = false) => {
      try {
          await api.put("/settings/customFieldRegistry", {
              value: JSON.stringify(newFields)
          });
          setFields(newFields);
          if (!silent) toast.success("Campos guardados");
      } catch (e) {
          toast.error("Error al guardar campos");
      }
  };

  const saveFolders = async (newFolders) => {
      try {
          await api.put("/settings/customFieldFolders", {
              value: JSON.stringify(newFolders)
          });
          setFolders(newFolders);
          toast.success("Carpetas guardadas");
      } catch (e) {
          toast.error("Error al guardar carpetas");
      }
  };

  const handleSaveField = async () => {
    if (!fieldName) return;
    
    const newField = { 
        id: editingField ? editingField.id : Date.now(),
        name: fieldName, 
        description: fieldDescription,
        type: fieldType,
        folderId: fieldFolder,
        system: editingField ? editingField.system : false 
    };

    let newFields = [...fields];

    if (editingField) {
        const index = newFields.findIndex(f => f.id === editingField.id);
        if (index !== -1) newFields[index] = newField;
    } else {
        newFields.push(newField);
    }

    await saveFields(newFields);
    handleCloseFieldModal();
  };

  const handleSaveFolder = async () => {
      if (!folderName) return;
      const newFolder = { id: Date.now(), name: folderName };
      const newFolders = [...folders, newFolder];
      await saveFolders(newFolders);
      handleCloseFolderModal();
  };

  const handleOpenDeleteDialog = (item, type) => {
      setItemToDelete(item);
      setDeleteType(type);
      setConfirmOpen(true);
  };

  const handleCloseDeleteDialog = () => {
      setConfirmOpen(false);
      setItemToDelete(null);
      setDeleteType("");
  };

  const executeDelete = async () => {
      if (deleteType === 'field') {
          const newFields = fields.filter(f => f.id !== itemToDelete.id);
          await saveFields(newFields);
      } else if (deleteType === 'folder') {
          const newFolders = folders.filter(f => f.id !== itemToDelete.id);
          await saveFolders(newFolders);
      }
      handleCloseDeleteDialog();
  };

  const handleDeleteField = (field) => {
      handleOpenDeleteDialog(field, 'field');
  };

  const handleOpenFieldModal = (field = null) => {
      if (field) {
          setEditingField(field);
          setFieldName(field.name);
          setFieldDescription(field.description || "");
          setFieldType(field.type || "Texto");
          setFieldFolder(field.folderId || "");
      } else {
          setEditingField(null);
          setFieldName("");
          setFieldDescription("");
          setFieldType("Texto");
          setFieldFolder("");
      }
      setFieldModalOpen(true);
  };

  const handleCloseFieldModal = () => {
      setFieldModalOpen(false);
      setEditingField(null);
  };

  const handleOpenFolderModal = () => {
      setFolderName("");
      setFolderModalOpen(true);
  };

  const handleCloseFolderModal = () => {
      setFolderModalOpen(false);
  };

  const getFolderName = (id) => {
      const f = folders.find(folder => folder.id === id);
      return f ? f.name : "";
  };

  const getFieldsByFolder = () => {
      const grouped = {};
      
      // Initialize with System folder
      grouped[SYSTEM_FOLDER.id] = { folder: SYSTEM_FOLDER, fields: [] };
      
      // Initialize with user folders
      folders.forEach(f => {
          grouped[f.id] = { folder: f, fields: [] };
      });
      
      // Initialize Unassigned
      grouped[UNASSIGNED_FOLDER.id] = { folder: UNASSIGNED_FOLDER, fields: [] };

      // Distribute fields
      fields.forEach(f => {
          const matchesSearch = f.name && f.name.toLowerCase().includes(search.toLowerCase());
          if (!matchesSearch) return;

          if (f.system) {
              grouped[SYSTEM_FOLDER.id].fields.push(f);
          } else if (f.folderId && grouped[f.folderId]) {
              grouped[f.folderId].fields.push(f);
          } else {
              grouped[UNASSIGNED_FOLDER.id].fields.push(f);
          }
      });

      return grouped;
  };

  const groupedFields = getFieldsByFolder();
  const folderKeys = [
      SYSTEM_FOLDER.id, 
      ...folders.map(f => f.id), 
      UNASSIGNED_FOLDER.id
  ].filter(key => groupedFields[key] && groupedFields[key].fields.length > 0);

  return (
    <MainContainer>
        <MainHeader>
            <Title>Campos personalizados</Title>
            <MainHeaderButtonsWrapper>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenFieldModal()}
                >
                    CREAR CAMPO DEL USUARIO
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenFolderModal}
                    startIcon={<CreateNewFolderIcon />}
                    style={{ backgroundColor: 'white' }}
                >
                    NUEVA CARPETA
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setFolderManagementOpen(true)}
                    style={{ backgroundColor: 'white' }}
                >
                    ADMINISTRAR CARPETAS
                </Button>
                <TextField
                    placeholder="Buscar"
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                    style={{ marginLeft: 8, backgroundColor: 'white' }}
                />
            </MainHeaderButtonsWrapper>
        </MainHeader>

        <Paper variant="outlined" style={{ margin: 16 }}>
            <Table className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {folderKeys.map(key => {
                        const group = groupedFields[key];
                        const isExpanded = expandedFolders[key];

                        return (
                            <React.Fragment key={key}>
                                {/* Folder Header Row */}
                                <TableRow 
                                    className={classes.folderHeader} 
                                    onClick={() => toggleFolder(key)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <TableCell colSpan={5}>
                                        <Box display="flex" alignItems="center">
                                            <IconButton size="small" style={{ marginRight: 8 }}>
                                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                            <Folder className={classes.folderIcon} />
                                            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                                {group.folder.name} ({group.fields.length})
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>

                                {/* Fields Rows */}
                                {isExpanded && group.fields.map((field) => (
                                    <TableRow key={field.id} className={classes.folderRow}>
                                        <TableCell padding="checkbox">
                                            <Checkbox style={{ marginLeft: 32 }} />
                                        </TableCell>
                                        <TableCell>
                                            {field.name}
                                            {field.system && (
                                                <Chip label="Sistema" variant="outlined" color="primary" size="small" className={classes.chip} />
                                            )}
                                        </TableCell>
                                        <TableCell>{field.description}</TableCell>
                                        <TableCell>{field.type}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleOpenFieldModal(field)} disabled={field.system}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDeleteField(field)} style={{ color: field.system ? 'gray' : 'red' }} disabled={field.system}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </Paper>

        {/* Folder Management Modal */}
        <Dialog open={folderManagementOpen} onClose={() => setFolderManagementOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Gestionar Carpetas</DialogTitle>
            <DialogContent dividers>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={2} align="center">No hay carpetas creadas</TableCell>
                            </TableRow>
                        ) : (
                            folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').map(f => (
                                <TableRow key={f.id}>
                                    <TableCell>{f.name}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => {
                                            setItemToDelete(f);
                                            setDeleteType('folder');
                                            setConfirmOpen(true);
                                        }} style={{ color: 'red' }}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFolderManagementOpen(false)} color="primary">
                    CERRAR
                </Button>
            </DialogActions>
        </Dialog>

        {/* Field Modal */}
        <Dialog open={fieldModalOpen} onClose={handleCloseFieldModal} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editingField ? "Editar campo" : "Crear nuevo campo"}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Nombre"
                    value={fieldName}
                    onChange={(e) => setFieldName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
                <TextField
                    label="Descripción"
                    value={fieldDescription}
                    onChange={(e) => setFieldDescription(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                        value={fieldType}
                        onChange={(e) => setFieldType(e.target.value)}
                        label="Tipo"
                    >
                        <MenuItem value="Texto">Texto</MenuItem>
                        <MenuItem value="Fecha">Fecha</MenuItem>
                        <MenuItem value="Numero">Número</MenuItem>
                        <MenuItem value="Lista">Lista</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Carpeta</InputLabel>
                    <Select
                        value={fieldFolder}
                        onChange={(e) => setFieldFolder(e.target.value)}
                        label="Carpeta"
                    >
                        <MenuItem value=""><em>Ninguna</em></MenuItem>
                        {folders.filter(f => f.id !== SYSTEM_FOLDER.id && f.name !== 'Sistema').map(f => (
                            <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseFieldModal} color="primary">
                    CANCELAR
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveField}
                >
                    {editingField ? "GUARDAR CAMPO" : "CREAR CAMPO"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Folder Modal */}
        <Dialog open={folderModalOpen} onClose={handleCloseFolderModal} maxWidth="sm" fullWidth>
            <DialogTitle>Crear carpeta</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Ingrese el nombre"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseFolderModal} color="primary">
                    CANCELAR
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveFolder}
                >
                    CREAR CARPETA
                </Button>
            </DialogActions>
        </Dialog>

        {/* Confirmation Modal */}
        <ConfirmationModal
            open={confirmOpen}
            onClose={setConfirmOpen}
            onConfirm={executeDelete}
            title="Confirmar Eliminación"
        >
            <Typography variant="body1" gutterBottom>
                ¿Estás seguro de que deseas eliminar este {deleteType === 'field' ? 'campo' : 'carpeta'}?
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                Esta acción no se puede deshacer.
            </Typography>
            {itemToDelete && (
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    {itemToDelete.name}
                </Typography>
            )}
        </ConfirmationModal>

    </MainContainer>
  );
};

export default CustomFieldsSettings;
